import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import Modal from "../../components/UI/Modal";
import Button from "../../components/UI/Button";
import SingleUsedVehicleCard from "../../components/Vehicles/SingleUsedVehicleCard";
import { selectUser } from "../../store/userSlice";
import { VehicleNames } from "../../utils/GeneralTypes";
import { getWishlistVehicles, updateWishlist } from "../VehiclePage/utils";
import style from "./index.module.css";
import VehicleTypesControllers from "../../components/General/VehiclesControllers";
import SingleVehicleCard from "../../components/Vehicles/SingleVehicleCard";
import { useScrollToTop } from "../../utils/generalUtils";
import EmptyContent from "../../components/General/EmptyContent";
import { useTranslation } from "../../hooks/useTranslation";
import { useHistory } from "react-router-dom";

const Wishlist = ({ vehicleNames, vehicleState }: ComponentPropsType) => {
  const { user } = useSelector(selectUser);
  const { locale } = useTranslation();
  const history = useHistory();

  const initialMount = useRef<boolean>(true);

  useScrollToTop();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: {
      vehicles: any[];
    };
  }>({
    isFetching: true,
    data: {
      vehicles: [],
    },
  });

  const [isModalActive, setIsModalActive] = useState<number>(-1);

  useEffect(() => {
    if (!user) {
      history.replace("/sign");
    } else {
      (async () => {
        if (!initialMount.current) {
          setComponentData((current) => ({
            ...current,
            isFetching: true,
          }));
        }
        initialMount.current = false;
        // TODO: add type
        let wishlistVehicles: any = [];
        await getWishlistVehicles(vehicleNames.single)
          .then((res) => {
            wishlistVehicles = res.data.wishlist.data;
          })
          .catch(() => {});
        setComponentData({
          isFetching: false,
          data: {
            vehicles: wishlistVehicles,
          },
        });
      })();
    }
  }, [vehicleNames, vehicleState, user, history]);

  const onRemoveWishlistHandler = useCallback(async () => {
    await updateWishlist(vehicleNames.single, "delete", isModalActive.toString()).catch(() => {});
    setComponentData((currentState) => ({
      ...currentState,
      data: {
        ...currentState.data,
        vehicles: currentState.data.vehicles.filter((el) => el.id.toString() !== isModalActive.toString()),
      },
    }));
    setIsModalActive(-1);
  }, [vehicleNames, isModalActive]);

  return (
    <>
      {isModalActive !== -1 && (
        <Modal onClose={() => setIsModalActive(-1)}>
          <div className={style.modal}>
            <p className={style.modal_title}>{locale(`${vehicleNames.single}_sure_to_remove_wishlist`)}</p>
            <div className={style.modal_ctas}>
              <Button styleType="primary" onClick={onRemoveWishlistHandler}>
                {locale("remove")}
              </Button>
              <Button styleType="primary_black" onClick={() => setIsModalActive(-1)}>
                {locale("cancel")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                {
                  text: locale("wishlist"),
                  link: "",
                },
              ]}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <div className={style.section_header}>
              <h3 className="main_section__title">{locale("wishlist")}</h3>
              <VehicleTypesControllers baseLink="/wishlist" postLink="used" active={vehicleNames.single} />
            </div>
            <div className={`${style.all_vehicles} row`}>
              {componentData.data.vehicles.length === 0 && !componentData.isFetching && (
                <div className="col-12">
                  <EmptyContent>
                    <p>{locale("no_wishlist").replace("%vehicles%", locale(vehicleNames.plural).toLowerCase())}.</p>
                  </EmptyContent>
                </div>
              )}
              {((componentData.data.vehicles.length > 0 && !componentData.isFetching) || componentData.isFetching) &&
                (componentData.isFetching ? (
                  <div className="col-12">
                    <LoadingSkeleton count={8} />
                  </div>
                ) : (
                  componentData.data.vehicles.map((item: any) => (
                    <div className="col-md-4 col-sm-6" key={item.id}>
                      {vehicleState === "new" ? (
                        <SingleVehicleCard
                          brand={{
                            image: item?.model?.brand?.logo,
                            link: `/new-${vehicleNames.plural}/brands/${item?.model?.brand?.id}`,
                            name: item?.model?.brand?.name,
                          }}
                          has_discount={item.has_discount}
                          has_free_maintenance={item?.has_free_maintenance}
                          image={item?.images?.[0]?.image}
                          link={`/new-${vehicleNames.plural}/${vehicleNames.single}/${item.id}`}
                          model={item?.model?.name}
                          old_price={item?.old_price}
                          price={item.price}
                          year={item?.model?.year}
                          isWishlistPage={true}
                          removeWishList={() => setIsModalActive(item.id)}
                          selling_flag={item?.selling_flag}
                        />
                      ) : (
                        <SingleUsedVehicleCard
                          showroom={{
                            is_showroom: vehicleState === "imported" ? 1 : item?.seller?.is_showroom,
                            image: item?.showroom?.logo || item?.seller?.showroom?.logo,
                            link: `/showrooms/showroom/${item?.showroom?.id}`,
                          }}
                          image={item?.images?.[0]?.image}
                          link={`/${item.condition.code}-${vehicleNames.plural}/${vehicleNames.single}/${item.id}`}
                          isFeatured={item.listing_period.is_featured}
                          date={item?.created_at}
                          location={item?.region ? `${item?.region?.name}, ${item?.region?.city?.name}` : ""}
                          name={`${item?.model?.brand?.name} ${item?.model?.name} ${item?.model?.year}`}
                          price={item?.price}
                          phone={item?.seller.is_showroom ? item?.seller?.branch?.phone : undefined}
                          isWishlistPage={true}
                          removeWishList={() => setIsModalActive(item.id)}
                        />
                      )}
                    </div>
                  ))
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Wishlist;

type ComponentPropsType = {
  vehicleNames: VehicleNames;
  vehicleState: "new" | "used" | "imported";
};
